let Wizard = class {
  // Configuration options with defaults
  defaultOptions = {
    container: null,
    steps: [],
    allowClickableSteps: true,
    validateOnStepChange: true,
    showStepNumbers: true,
    showStepLabels: true,
    compactMode: false,
    verticalLayout: false,
    onStepChange: null,
    onComplete: null,
    onValidationError: null
  };
  
  // Current state
  currentStep = 1;
  completedSteps = [];
  visitedSteps = [1]; // Track which steps have been visited
  stepData = {};
  
  constructor(options = {}) {
    this.options = { ...this.defaultOptions, ...options };
    
    if (!this.options.container) {
      throw new Error('Wizard requires a container element');
    }
    
    this.container = typeof this.options.container === 'string' 
      ? document.querySelector(this.options.container)
      : this.options.container;
      
    if (!this.container) {
      throw new Error('Wizard container not found');
    }
    
    this.init();
  }
  
  init() {
    this.render();
    this.bindEvents();
    this.showStep(this.currentStep);
  }
  
  render() {
    // Check if container is already a step-wizard
    const isStepWizard = this.container.classList.contains('step-wizard');
    
    // Create wizard structure
    const wizardClass = `step-wizard${this.options.verticalLayout ? ' vertical' : ''}${this.options.compactMode ? ' compact' : ''}`;
    
    if (isStepWizard) {
      // Container is already a step-wizard, just add the steps
      // Preserve existing classes while ensuring wizard classes are applied
      this.container.classList.add(...wizardClass.split(' ').filter(c => c));
      this.container.innerHTML = this.options.steps.map((step, index) => this.renderStep(step, index + 1)).join('');
    } else {
      // Container is not a step-wizard, create the wrapper
      const wizardHTML = `
        <div class="${wizardClass}">
          ${this.options.steps.map((step, index) => this.renderStep(step, index + 1)).join('')}
        </div>
      `;
      
      this.container.innerHTML = wizardHTML;
    }
    
    // Cache element references
    this.el = {
      wizard: isStepWizard ? this.container : this.container.querySelector('.step-wizard'),
      steps: this.container.querySelectorAll('.wizard-step'),
      stepNumbers: this.container.querySelectorAll('.step-number'),
      stepContents: this.container.querySelectorAll('.step-content')
    };
  }
  
  renderStep(step, number) {
    const stepClass = this.getStepClass(number);
    
    return `
      <div class="wizard-step ${stepClass}" data-step="${number}">
        <div class="step-number">
          ${step.icon ? `<i class="${step.icon}"></i>` : number}
        </div>
        <div class="step-content">
          ${this.options.showStepLabels ? `<div class="step-title">${step.label}</div>` : ''}
          ${step.description && !this.options.compactMode ? `<div class="step-description">${step.description}</div>` : ''}
        </div>
      </div>
    `;
  }
  
  getStepClass(stepNumber) {
    // Current step always takes precedence (purple)
    if (stepNumber === this.currentStep) {
      return 'current';
    } else if (this.completedSteps.includes(stepNumber)) {
      // Completed steps show as active (green checkmark)
      return 'active';
    } else if (this.visitedSteps.includes(stepNumber)) {
      // Previously visited steps are clickable but not marked as complete
      return 'visited';
    } else if (stepNumber === this.currentStep + 1) {
      // Next step is always clickable
      return 'next-step';
    } else {
      return 'future-step non-clickable';
    }
  }
  
  bindEvents() {
    if (this.options.allowClickableSteps) {
      this.el.steps.forEach((step, index) => {
        step.addEventListener('click', () => {
          const stepNumber = index + 1;
          
          // Allow clicking on:
          // 1. Current step
          // 2. Any visited step (previously navigated to)
          // 3. The immediate next step
          if (stepNumber === this.currentStep || 
              this.visitedSteps.includes(stepNumber) ||
              stepNumber === this.currentStep + 1) {
            this.goToStep(stepNumber);
          }
        });
      });
    }
  }
  
  // Public API methods
  
  next() {
    if (this.currentStep < this.options.steps.length) {
      this.goToStep(this.currentStep + 1);
    }
  }
  
  prev() {
    if (this.currentStep > 1) {
      this.goToStep(this.currentStep - 1);
    }
  }
  
  goToStep(stepNumber) {
    if (stepNumber < 1 || stepNumber > this.options.steps.length) {
      return false;
    }
    
    // Always validate current step before leaving when moving forward
    // This ensures validation happens even if going through previously completed steps
    if (this.options.validateOnStepChange && stepNumber > this.currentStep) {
      const validation = this.validateCurrentStep();
      if (!validation.valid) {
        if (this.options.onValidationError) {
          this.options.onValidationError(validation);
        }
        return false;
      }
    }
    
    // Mark current step as completed only if moving forward AND validation passed
    if (stepNumber > this.currentStep && !this.completedSteps.includes(this.currentStep)) {
      this.completedSteps.push(this.currentStep);
    }
    
    // Track visited steps - mark the target step as visited
    if (!this.visitedSteps.includes(stepNumber)) {
      this.visitedSteps.push(stepNumber);
    }
    
    // Update current step
    const oldStep = this.currentStep;
    this.currentStep = stepNumber;
    
    // Update UI
    this.updateStepClasses();
    this.showStep(stepNumber);
    
    // Trigger callback
    if (this.options.onStepChange) {
      this.options.onStepChange({
        from: oldStep,
        to: stepNumber,
        step: this.options.steps[stepNumber - 1],
        isComplete: this.isComplete()
      });
    }
    
    // Check if wizard is complete
    if (this.isComplete() && this.options.onComplete) {
      this.options.onComplete(this.getData());
    }
    
    return true;
  }
  
  showStep(stepNumber) {
    // This method can be overridden to show/hide step content
    // By default, it just updates the wizard display
    this.container.dispatchEvent(new CustomEvent('wizard:stepShown', {
      detail: { 
        step: stepNumber,
        wizard: this
      }
    }));
  }
  
  updateStepClasses() {
    this.el.steps.forEach((step, index) => {
      const stepNumber = index + 1;
      step.className = `wizard-step ${this.getStepClass(stepNumber)}`;
    });
  }
  
  validateCurrentStep() {
    const step = this.options.steps[this.currentStep - 1];
    
    if (step.validate && typeof step.validate === 'function') {
      const result = step.validate(this.stepData[this.currentStep]);
      
      // Ensure the result is in the expected format
      if (typeof result === 'boolean') {
        return { valid: result };
      } else if (result && typeof result === 'object') {
        return result;
      }
    }
    
    // Default: always valid
    return { valid: true };
  }
  
  isComplete() {
    return this.completedSteps.length === this.options.steps.length - 1 && 
           this.currentStep === this.options.steps.length;
  }
  
  // Data management
  
  setStepData(stepNumber, data) {
    this.stepData[stepNumber] = data;
  }
  
  getStepData(stepNumber) {
    return this.stepData[stepNumber] || {};
  }
  
  getData() {
    return this.stepData;
  }
  
  // Progress methods
  
  getCurrentStep() {
    return this.currentStep;
  }
  
  getProgress() {
    return {
      current: this.currentStep,
      total: this.options.steps.length,
      completed: this.completedSteps.length,
      percentage: Math.round((this.completedSteps.length / this.options.steps.length) * 100)
    };
  }
  
  reset() {
    this.currentStep = 1;
    this.completedSteps = [];
    this.visitedSteps = [1];
    this.stepData = {};
    this.updateStepClasses();
    this.showStep(1);
  }
  
  // Utility methods
  
  markStepComplete(stepNumber) {
    if (!this.completedSteps.includes(stepNumber)) {
      this.completedSteps.push(stepNumber);
      this.updateStepClasses();
    }
  }
  
  markStepIncomplete(stepNumber) {
    const index = this.completedSteps.indexOf(stepNumber);
    if (index > -1) {
      this.completedSteps.splice(index, 1);
      this.updateStepClasses();
    }
  }
  
  enableStep(stepNumber) {
    const step = this.el.steps[stepNumber - 1];
    if (step) {
      step.classList.remove('disabled');
    }
  }
  
  disableStep(stepNumber) {
    const step = this.el.steps[stepNumber - 1];
    if (step) {
      step.classList.add('disabled');
    }
  }
  
  updateStep(stepNumber, updates) {
    const step = this.options.steps[stepNumber - 1];
    if (step) {
      Object.assign(step, updates);
      
      // Re-render just this step
      const stepEl = this.el.steps[stepNumber - 1];
      if (stepEl) {
        stepEl.outerHTML = this.renderStep(step, stepNumber);
        
        // Re-cache elements and re-bind events
        this.el.steps = this.container.querySelectorAll('.wizard-step');
        this.bindEvents();
        this.updateStepClasses();
      }
    }
  }
};

// Export for use in other modules
window.sflow = window.sflow || {};
window.sflow.Wizard = Wizard;